import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router";
import { AdminAlert } from "@/components/shell/AdminPage";
import { useAccountWorkspace } from "@/lib/account-workspace";
import { BrandSiteApiError, fetchBrandSites } from "@/lib/brand-site-api";
import {
  brandSiteListPath,
  brandSitePath,
  brandSiteSectionFromPathname,
  primaryBrandSiteId,
  type BrandSiteSection,
} from "@/lib/brand-site-routing";

/** 旧产品面板上的列表 URL → 品牌容器内的列表 */
export function BrandSiteListRedirect() {
  const { accountId, brandId } = useParams();
  if (!accountId || !brandId) return null;
  return <Navigate to={brandSiteListPath(accountId, brandId)} replace />;
}

/** 旧无 siteId 的设计/页面/发布/域名：有站落到主站；无站回列表 */
export default function BrandSiteLegacyRedirect({ section }: { section?: BrandSiteSection }) {
  const { brandId } = useParams();
  const { accountId } = useAccountWorkspace();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [error, setError] = useState<string | null>(null);

  const resolvedSection = section ?? brandSiteSectionFromPathname(pathname);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      if (!brandId) return;
      setError(null);
      try {
        const listed = await fetchBrandSites(accountId, brandId);
        const sites = listed.brand_sites ?? [];
        const siteId = primaryBrandSiteId(sites);
        if (cancelled) return;
        if (!siteId) {
          navigate(brandSiteListPath(accountId, brandId), { replace: true });
          return;
        }
        navigate(brandSitePath(accountId, brandId, siteId, resolvedSection), { replace: true });
      } catch (caught) {
        if (cancelled) return;
        const message =
          caught instanceof BrandSiteApiError
            ? caught.message
            : caught instanceof TypeError
              ? import.meta.env.DEV
                ? "暂时连不上服务。本地请先启动品牌站（:3020）。"
                : "暂时连不上服务，请稍后重试"
              : caught instanceof Error
                ? caught.message
                : "暂时没法打开，请稍后重试";
        setError(message);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accountId, brandId, navigate, resolvedSection]);

  if (error) {
    return <AdminAlert>{error}</AdminAlert>;
  }

  return <p className="text-sm text-muted-foreground">正在打开官网…</p>;
}
